import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal, KanbanSquare } from 'lucide-react';
import { useDeals } from '@/composables/crm/useDeals';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Deal } from '@/types/table';

export function DealListPage() {
  const navigate = useNavigate();
  const { deals, meta, isLoading, filters, exportCsv, deleteDeal, markWon, markLost } = useDeals();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Deal | null>(null);

  const columns: ColumnDef<Deal, unknown>[] = useMemo(() => [
    { accessorKey: 'title', header: ({ column }) => <DataTableColumnHeader column={column} title="Title" />, cell: ({ row }) => <span className="font-medium">{row.original.title}</span> },
    { accessorKey: 'value', header: ({ column }) => <DataTableColumnHeader column={column} title="Value" />, cell: ({ row }) => formatCurrency(row.original.value, row.original.currency) },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} type="deal" /> },
    { accessorKey: 'stage', header: 'Stage', cell: ({ row }) => row.original.stage?.name || '-' },
    { accessorKey: 'company', header: 'Company', cell: ({ row }) => row.original.company?.name || '-' },
    { accessorKey: 'expectedCloseDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Close Date" />, cell: ({ row }) => formatDate(row.original.expectedCloseDate) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/deals/${row.original.id}`); }}>View</DropdownMenuItem>
            {row.original.status === 'OPEN' && (
              <>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); markWon.mutate(row.original.id); }}>Mark Won</DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); markLost.mutate({ id: row.original.id }); }}>Mark Lost</DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Deals" description="Table view of all deals" actions={<><Button variant="outline" onClick={() => navigate('/deals/pipeline')}><KanbanSquare className="mr-2 h-4 w-4" />Pipeline View</Button><Button variant="outline" onClick={() => exportFile(exportCsv, 'deals.csv')} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button><Button onClick={() => navigate('/deals/new')}><Plus className="mr-2 h-4 w-4" />New Deal</Button></>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search deals..." />
      <DataTable columns={columns} data={deals} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/deals/${row.id}`)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Deal"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteDeal.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteDeal.isPending}
      />
    </PageContainer>
  );
}
